// 종목 시세 그래프 팝업
// turns prop 있음 → 다차수 모드 (TurnStatusPage)
// buyPrice prop만 있음 → 단일 기준선 모드 (기타 화면)
import React, { useState, useEffect, useRef } from 'react';
import {
  ComposedChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ReferenceLine, ResponsiveContainer,
} from 'recharts';
import axiosInstance from '../../api/axiosInstance';
import './StockChartPopup.css';

const PERIODS = [
  { key: '1m', label: '1개월' },
  { key: '3m', label: '3개월' },
  { key: '6m', label: '6개월' },
  { key: '1y', label: '1년' },
  { key: '3y', label: '3년' },
  { key: '5y', label: '5년' },
];

const TURN_COLORS = ['#e34948', '#e87ba4', '#eda100', '#1baf7a'];
const turnColor = n => TURN_COLORS[(n - 1) % 4];

const PERIOD_INTERVAL = { '1m': 4, '3m': 13, '6m': 25, '1y': 50, '3y': 130, '5y': 260 };

// ── 포맷 헬퍼 ─────────────────────────────────────────────
const fPrice = v => {
  if (v == null) return '—';
  const n = Number(v);
  return n % 1 !== 0
    ? n.toLocaleString('ko-KR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : n.toLocaleString('ko-KR');
};
const fRate = v => {
  if (v == null) return '';
  const n = Number(v);
  return `${n > 0 ? '+' : ''}${n.toFixed(2)}%`;
};
const fQty = v => {
  if (v == null) return '—';
  const n = Number(v);
  return n % 1 !== 0
    ? n.toLocaleString('ko-KR', { maximumFractionDigits: 4 })
    : n.toLocaleString('ko-KR');
};
const fDate = d => {
  if (!d) return '';
  return `${d.substring(2, 4)}.${d.substring(5, 7)}.${d.substring(8, 10)}`;
};

const formatTick = (dateStr, period) => {
  if (!dateStr) return '';
  const m  = dateStr.substring(5, 7);
  const d  = dateStr.substring(8, 10);
  const yy = dateStr.substring(2, 4);
  if (period === '1m' || period === '3m') return `${parseInt(m)}/${parseInt(d)}`;
  if (period === '6m' || period === '1y') return `${yy}.${m}`;
  return `${dateStr.substring(0, 4)}.${m}`;
};

const findClosestDate = (dateStr, dateList) => {
  if (!dateList?.length || !dateStr) return null;
  const target = new Date(dateStr).getTime();
  return dateList.reduce((best, d) => {
    const bestDiff = Math.abs(new Date(best).getTime() - target);
    const currDiff = Math.abs(new Date(d).getTime() - target);
    return currDiff < bestDiff ? d : best;
  }, dateList[0]);
};

const fAxisPrice = v => {
  if (v == null) return '';
  const n = Number(v);
  if (n >= 100000) return `${Math.round(n / 10000)}만`;
  if (n >= 10000)  return `${(n / 10000).toFixed(1)}만`;
  return n.toLocaleString('ko-KR');
};

// ── 수평 기준선 라벨 (매입단가) ───────────────────────────
const HRefLabel = ({ viewBox, color, caption }) => {
  if (!viewBox) return null;
  const { x, y, width } = viewBox;
  return (
    <text x={x + width - 4} y={y - 4}
      textAnchor="end" fontSize={10} fill={color} fontWeight="700">
      {caption}
    </text>
  );
};

// ── 수직 기준선 라벨 (매수일) ─────────────────────────────
const VRefLabel = ({ viewBox, color, caption }) => {
  if (!viewBox) return null;
  const { x, y, height } = viewBox;
  return (
    <text x={x + 3} y={y + height - 5}
      textAnchor="start" fontSize={10} fill={color} fontWeight="700">
      {caption}
    </text>
  );
};

// ── 커스텀 툴팁 ───────────────────────────────────────────
const CustomTooltip = ({ active, payload, label, turns }) => {
  if (!active || !payload?.length) return null;
  const price = payload[0]?.value;
  if (price == null) return null;
  return (
    <div style={{
      background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8,
      padding: '8px 12px', fontSize: 12, minWidth: 130,
      boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
    }}>
      <div style={{ fontWeight: 600, marginBottom: 5, color: '#555' }}>{label}</div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, marginBottom: 3 }}>
        <span style={{ color: '#888' }}>시세</span>
        <span style={{ fontWeight: 700, color: '#1a1a2e' }}>{fPrice(price)}</span>
      </div>
      {turns.filter(t => t.buy_price != null).map(t => {
        const rate = price > 0 && t.buy_price > 0
          ? (price - t.buy_price) / t.buy_price * 100 : null;
        const color = turnColor(t.turn_no);
        const rateColor = rate == null ? '#666' : rate >= 0 ? '#e53935' : '#1565c0';
        return (
          <div key={t.turn_no} style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
            <span style={{ color }}>{t.turn_no}차</span>
            <span style={{ color: rateColor, fontWeight: 600 }}>
              {rate != null ? fRate(rate) : '—'}
            </span>
          </div>
        );
      })}
    </div>
  );
};

// ════════════════════════════════════════════════════════════
// 메인 컴포넌트
// ════════════════════════════════════════════════════════════
const StockChartPopup = ({
  ticker,
  stockName,
  // 다차수 모드 (TurnStatusPage)
  accountName,
  termType,
  currentPrice,
  diffRate,
  turns = [],
  // 레거시 단일 기준선 모드 (기타 화면)
  buyPrice = 0,
  buyDate = null,
  onClose,
}) => {
  const [period,    setPeriod]    = useState('3m');
  const [chartData, setChartData] = useState([]);
  const [loading,   setLoading]   = useState(false);
  const overlayRef = useRef(null);

  // turns 없으면 buyPrice/buyDate로 단일 차수 구성
  const effectiveTurns = turns.length > 0
    ? turns
    : buyPrice > 0
      ? [{ turn_no: 1, buy_price: buyPrice, buy_date: buyDate, quantity: null, profit_rate: null }]
      : [];

  const unsoldTurns = effectiveTurns.filter(t => t.buy_price != null);
  const dateList    = chartData.map(d => d.date);
  const diffColor   = diffRate == null ? '#333' : Number(diffRate) >= 0 ? '#e53935' : '#1565c0';

  // ESC 닫기
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // 시세 이력 로드
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!ticker) return;
      setLoading(true);
      setChartData([]);
      try {
        const res = await axiosInstance.get('/mobile/stock-chart', { params: { ticker, period } });
        if (!cancelled) setChartData(res.data?.price_history || []);
      } catch {
        if (!cancelled) setChartData([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [ticker, period]);

  const handleOverlayClick = e => {
    if (e.target === overlayRef.current) onClose();
  };

  return (
    <div className="scp-overlay" ref={overlayRef} onClick={handleOverlayClick}>
      <div className="scp-panel">

        {/* ── 헤더 ── */}
        <div className="scp-header">
          <div className="scp-title-block">
            <div className="scp-title">
              <span className="scp-stock-name">{stockName || ticker}</span>
              <span className="scp-ticker">{ticker}</span>
            </div>
            {(accountName || termType) && (
              <div className="scp-account-sub">
                {[accountName, termType].filter(Boolean).join(' · ')}
              </div>
            )}
          </div>
          <div className="scp-header-right">
            {currentPrice != null && (
              <div className="scp-current-price-block">
                <span className="scp-current-price">{fPrice(currentPrice)}</span>
                {diffRate != null && (
                  <span className="scp-diff-rate" style={{ color: diffColor }}>
                    {fRate(diffRate)}
                  </span>
                )}
              </div>
            )}
            <button className="scp-close" onClick={onClose}>✕</button>
          </div>
        </div>

        {/* ── 기간 선택 ── */}
        <div className="scp-period-bar">
          {PERIODS.map(p => (
            <button
              key={p.key}
              className={`scp-period-btn${period === p.key ? ' active' : ''}`}
              onClick={() => setPeriod(p.key)}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* ── 스크롤 본문 ── */}
        <div className="scp-body">

          {/* 차트 */}
          <div className="scp-chart-wrap">
            {loading ? (
              <div className="scp-center">로딩 중...</div>
            ) : chartData.length === 0 ? (
              <div className="scp-center">시세 데이터가 없습니다.</div>
            ) : (
              <div style={{ padding: '12px 8px 4px 0' }}>
                <ResponsiveContainer width="100%" height={320}>
                  <ComposedChart
                    data={chartData}
                    margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 10, fill: '#aaa' }}
                      interval={PERIOD_INTERVAL[period] - 1}
                      tickFormatter={d => formatTick(d, period)}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      domain={['auto', 'auto']}
                      tick={{ fontSize: 10, fill: '#aaa' }}
                      width={56}
                      tickFormatter={fAxisPrice}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      content={<CustomTooltip turns={unsoldTurns} />}
                      cursor={{ stroke: '#ddd', strokeWidth: 1 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="price"
                      stroke="#4a6cf7"
                      strokeWidth={1.5}
                      dot={false}
                      activeDot={{ r: 3, fill: '#4a6cf7' }}
                      isAnimationActive={false}
                    />

                    {/* 차수별 매입단가 수평선 */}
                    {unsoldTurns.map(t => {
                      const color = turnColor(t.turn_no);
                      return (
                        <ReferenceLine
                          key={`h-${t.turn_no}`}
                          y={t.buy_price}
                          stroke={color}
                          strokeDasharray="4 3"
                          strokeWidth={1.5}
                          label={<HRefLabel
                            color={color}
                            caption={`${t.turn_no}차 ${fPrice(t.buy_price)}`}
                          />}
                        />
                      );
                    })}

                    {/* 차수별 매수일 수직선 */}
                    {unsoldTurns.map(t => {
                      const closest = findClosestDate(t.buy_date, dateList);
                      if (!closest) return null;
                      const color = turnColor(t.turn_no);
                      return (
                        <ReferenceLine
                          key={`v-${t.turn_no}`}
                          x={closest}
                          stroke={color}
                          strokeDasharray="4 3"
                          strokeWidth={1}
                          strokeOpacity={0.6}
                          label={<VRefLabel
                            color={color}
                            caption={`${t.turn_no}차`}
                          />}
                        />
                      );
                    })}
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* 범례 */}
          {unsoldTurns.length > 0 && !loading && chartData.length > 0 && (
            <div className="scp-legend">
              <div className="scp-legend-item">
                <span className="scp-legend-line" />
                <span>시세</span>
              </div>
              {unsoldTurns.map(t => (
                <div key={t.turn_no} className="scp-legend-item">
                  <span className="scp-legend-dot"
                    style={{ background: turnColor(t.turn_no) }} />
                  <span>{t.turn_no}차 {fPrice(t.buy_price)}</span>
                </div>
              ))}
            </div>
          )}

          {/* 차수 요약 테이블 */}
          {unsoldTurns.length > 0 && (
            <div className="scp-turn-table-wrap">
              <table className="scp-turn-table">
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left' }}>차수</th>
                    <th style={{ textAlign: 'left' }}>매수일</th>
                    <th style={{ textAlign: 'right' }}>단가</th>
                    <th style={{ textAlign: 'right' }}>수량</th>
                    <th style={{ textAlign: 'right' }}>수익률</th>
                  </tr>
                </thead>
                <tbody>
                  {unsoldTurns.map(t => {
                    const color    = turnColor(t.turn_no);
                    const rateColor = t.profit_rate == null ? '#333'
                      : Number(t.profit_rate) >= 0 ? '#e53935' : '#1565c0';
                    return (
                      <tr key={t.turn_no}>
                        <td style={{ color, fontWeight: 700 }}>{t.turn_no}차</td>
                        <td style={{ color: '#666' }}>{fDate(t.buy_date)}</td>
                        <td style={{ textAlign: 'right' }}>{fPrice(t.buy_price)}</td>
                        <td style={{ textAlign: 'right', color: '#555' }}>
                          {fQty(t.quantity)}
                        </td>
                        <td style={{ textAlign: 'right', color: rateColor, fontWeight: 600 }}>
                          {t.profit_rate != null ? fRate(t.profit_rate) : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

        </div>{/* /scp-body */}
      </div>
    </div>
  );
};

export default StockChartPopup;
